/**
 * Trust facts shown on the homepage, About, the services hub and landing pages. Computed facts
 * come from catalog data at build time, so counts follow the data. Company-declared facts
 * (src/lib/companyDeclaredFacts.shared.js) are the owner's statements and are always rendered
 * with their source caption; partnerships, ratings and company-level certifications stay absent
 * (docs/audit/2026-10-23-cursor-package/evidence/outputs/2026-11-02-trust-data-needed.md).
 */
import { BLOG_PUBLISHED_POST_COUNT } from '@/data/blogTagPages.generated.js';
import { toolsData } from '@/data/tools.data.mjs';
import { ABOUT_SECTORS } from '@/lib/aboutContent';
import { FIGTURES_FOUNDING_YEAR } from '@/lib/brandCopy';
import { CASE_STUDY_DETAILS } from '@/lib/caseStudyDetails';
import {
  COMPANY_CLIENT_COUNTRIES,
  COMPANY_DECLARED_COUNTS,
  COMPANY_DECLARED_SOURCE,
  DELIVERY_OUTCOME_APPROACH,
  formatClientCountries,
  formatTeamCertifications,
  getTechnologyGroups,
} from '@/lib/companyDeclaredFacts.shared.js';
import { FIGTURES_CLUTCH_URL } from '@/lib/entityProfiles';
import { getAllPillarGuides } from '@/lib/pillarGuideCatalog';
import { getPortfolioProjectSlug, portfolioProjects } from '@/lib/portfolioProjects';
import { TESTIMONIALS } from '@/lib/testimonials';

type L = { tr: string; en: string };
export type TrustLang = 'tr' | 'en';

export type TrustFact = {
  id: 'founded' | 'projects' | 'sectors' | 'tools' | 'content' | 'languages';
  value: string;
  label: string;
  detail?: string;
  href?: string;
};

export type DeclaredTrustFact = {
  id: 'completedProjects' | 'clients' | 'clientCountries' | 'team';
  value: string;
  label: string;
  detail?: string;
  source: 'company-declared';
};

export type DeclaredTrustStatement = {
  id: 'certifications' | 'technologies' | 'outcomes';
  label: string;
  text: string;
  groups?: Array<{ id: string; label: string; items: string[] }>;
  source: 'company-declared';
};

export type TrustObservation = {
  value: string;
  label: string;
  source: string;
  href: string;
};

const LANGUAGES: L[] = [
  { tr: 'Türkçe', en: 'Turkish' },
  { tr: 'İngilizce', en: 'English' },
];

function uniqueLocalized(values: L[], lang: TrustLang): string[] {
  return [...new Set(values.map((value) => value[lang]))];
}

function joinList(items: string[], lang: TrustLang): string {
  if (items.length < 2) return items.join('');
  const last = items[items.length - 1];
  return `${items.slice(0, -1).join(', ')} ${lang === 'tr' ? 've' : 'and'} ${last}`;
}

/** Sectors of published projects only, in the About page wording. */
function getPublishedSectors(): L[] {
  const published = new Set(portfolioProjects.map((project) => project.key));
  return ABOUT_SECTORS.filter((entry) => published.has(entry.project)).map((entry) => entry.sector);
}

export function getTrustCounts() {
  const sectors = new Set(getPublishedSectors().map((sector) => sector.en));
  return {
    foundingYear: FIGTURES_FOUNDING_YEAR,
    publishedProjects: portfolioProjects.length,
    detailedCaseStudies: Object.keys(CASE_STUDY_DETAILS).length,
    sectors: sectors.size,
    freeTools: toolsData.length,
    blogPosts: BLOG_PUBLISHED_POST_COUNT,
    guides: getAllPillarGuides().length,
    languages: LANGUAGES.length,
  };
}

export function getClientCountries(lang: TrustLang): string[] {
  return uniqueLocalized(
    TESTIMONIALS.flatMap((entry) => (entry.clientCountry ? [entry.clientCountry] : [])),
    lang,
  );
}

export function getTrustFacts(lang: TrustLang): TrustFact[] {
  const counts = getTrustCounts();
  const tr = lang === 'tr';
  const sectorNames = uniqueLocalized(getPublishedSectors(), lang);
  const facts: TrustFact[] = [
    { id: 'founded', value: counts.foundingYear, label: tr ? 'Kuruluş yılı' : 'Founded', detail: tr ? 'İstanbul' : 'Istanbul' },
    {
      id: 'projects',
      value: String(counts.publishedProjects),
      label: tr ? 'Sitede yayınlanan proje' : 'Projects published on this site',
      detail: tr
        ? `${counts.detailedCaseStudies} tanesi ayrıntılı case study`
        : `${counts.detailedCaseStudies} with a detailed case study`,
      href: '/projects',
    },
    {
      id: 'sectors',
      value: String(counts.sectors),
      label: tr ? 'Projelerdeki sektör' : 'Sectors in our projects',
      detail: sectorNames.join(' · '),
    },
  ];
  facts.push(
    {
      id: 'tools',
      value: String(counts.freeTools),
      label: tr ? 'Ücretsiz araç' : 'Free tools',
      href: '/tools',
    },
    {
      id: 'content',
      value: String(counts.blogPosts),
      label: tr ? 'Blog yazısı' : 'Blog posts',
      detail: tr ? `ve ${counts.guides} derinlemesine rehber` : `plus ${counts.guides} in-depth guides`,
      href: '/blog',
    },
    {
      id: 'languages',
      value: String(counts.languages),
      label: tr ? 'Çalışma dili' : 'Working languages',
      detail: joinList(uniqueLocalized(LANGUAGES, lang), lang),
    },
  );
  return facts;
}

/** Owner statements from 2026-11-03; counts are declared lower bounds, so they keep the "+". */
export function getDeclaredTrustFacts(lang: TrustLang): DeclaredTrustFact[] {
  const c = COMPANY_DECLARED_COUNTS;
  const tr = lang === 'tr';
  const source = 'company-declared' as const;
  return [
    {
      id: 'completedProjects',
      value: `${c.completedProjects}+`,
      label: tr ? 'Tamamlanan proje' : 'Completed projects',
      detail: tr ? 'Sitede yayınlanmayanlar dahil' : 'Including projects not published on this site',
      source,
    },
    { id: 'clients', value: `${c.clients}+`, label: tr ? 'Müşteri' : 'Clients', source },
    {
      id: 'clientCountries',
      value: String(COMPANY_CLIENT_COUNTRIES.length),
      label: tr ? 'Müşteri ülkesi' : 'Client countries',
      detail: formatClientCountries(lang),
      source,
    },
    {
      id: 'team',
      value: String(c.fullTimeEmployees),
      label: tr ? 'Tam zamanlı ekip' : 'Full-time team',
      detail: tr
        ? `Ayrıca ${c.outsourcedSpecialists}+ uzmandan oluşan dış kaynak ağı`
        : `Plus an outsourced network of ${c.outsourcedSpecialists}+ specialists`,
      source,
    },
  ];
}

export function getDeclaredTrustStatements(lang: TrustLang): DeclaredTrustStatement[] {
  const tr = lang === 'tr';
  const source = 'company-declared' as const;
  const groups = getTechnologyGroups(lang);
  return [
    {
      id: 'certifications',
      label: tr ? 'Ekip sertifikaları' : 'Team certifications',
      text: formatTeamCertifications(lang),
      source,
    },
    { id: 'outcomes', label: tr ? 'Teslim yaklaşımı' : 'Delivery approach', text: DELIVERY_OUTCOME_APPROACH[lang], source },
    {
      id: 'technologies',
      label: tr ? 'Kullandığımız teknolojiler' : 'Technologies we work with',
      text: groups.map((group) => `${group.label}: ${group.items.join(', ')}`).join(' · '),
      groups,
      source,
    },
  ];
}

/** The one client-published metric we have, with its source and period (3D Yanında GSC). */
export function getTrustObservation(lang: TrustLang): TrustObservation | null {
  const study = CASE_STUDY_DETAILS.p1;
  const observation = study?.observation;
  if (!study || !observation) return null;
  const [clicks, , , position] = observation.metrics;
  if (!clicks || !position) return null;
  const tr = lang === 'tr';
  return {
    value: `${clicks.value[lang]} · ${position.value[lang]}`,
    label: tr
      ? `3D Yanında: teslim sonrası 3 ayda ${clicks.label.tr.toLocaleLowerCase('tr')} · ${position.label.tr.toLocaleLowerCase('tr')}`
      : `3D Yanında: ${clicks.label.en.toLowerCase()} · ${position.label.en.toLowerCase()} in the 3 months after delivery`,
    source: observation.source[lang],
    href: `/projects/${getPortfolioProjectSlug(study.project)}`,
  };
}

export const TRUST_COPY = {
  title: { tr: 'Doğrulanabilir bilgiler', en: 'Verifiable facts' },
  intro: {
    tr: 'Sayılar sitedeki proje, araç ve içerik kataloglarından otomatik hesaplanır.',
    en: 'Counts are calculated automatically from the project, tool and content catalogs on this site.',
  },
  declaredCaption: COMPANY_DECLARED_SOURCE.caption,
  clutch: { tr: 'Clutch profilimiz', en: 'Our Clutch profile' },
  clutchUrl: FIGTURES_CLUTCH_URL,
} satisfies Record<string, L | string>;
